import { DestroyRef, inject, provideAppInitializer } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { distinctUntilChanged } from 'rxjs';

import { AppState } from './initial-state';

type SessionMessage = { type: 'login'; userId: number } | { type: 'logout' };

interface TabSession {
  isLoggedIn: boolean;
  userId?: number;
}

export const SESSION_CHANNEL_NAME = 'str-session';

export const provideSessionSync = () =>
  provideAppInitializer(() => {
    if (typeof BroadcastChannel === 'undefined') {
      return;
    }

    const channel = new BroadcastChannel(SESSION_CHANNEL_NAME);
    let session: TabSession = { isLoggedIn: false };

    inject<Store<AppState>>(Store)
      .select((state): TabSession => ({
        isLoggedIn: state.auth.isLoggedIn,
        userId: state.user.userId,
      }))
      .pipe(
        distinctUntilChanged(
          (previous, next) =>
            previous.isLoggedIn === next.isLoggedIn &&
            previous.userId === next.userId
        ),
        takeUntilDestroyed()
      )
      .subscribe((next) => {
        if (
          next.isLoggedIn &&
          next.userId &&
          (!session.isLoggedIn || session.userId !== next.userId)
        ) {
          channel.postMessage({ type: 'login', userId: next.userId });
        }

        if (session.isLoggedIn && !next.isLoggedIn) {
          channel.postMessage({ type: 'logout' });
        }

        session = next;
      });

    channel.onmessage = ({ data }: MessageEvent<SessionMessage>) => {
      if (!session.isLoggedIn) {
        return;
      }

      if (data.type === 'logout' || data.userId !== session.userId) {
        window.location.reload();
      }
    };

    inject(DestroyRef).onDestroy(() => channel.close());
  });
